import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast, ToastContainer } from "react-toastify";

import { API, STRIPEPUBLICKEY } from "../../backend";
import authHeader from "../../services/auth-header";
import AuthService from "../../services/auth.service";
import GetCompanyInfoService from "../../services/company/getCompanyInfoService";
import { C, S } from "./styles";

// The "Manage License" tab — same underlying data as the existing
// /payment screen (Your Plan, Subscription Details, license usage,
// Cancel Subscription, Payment History), restyled to match the rest of
// the Subscription pages. License changes (increase/reduce) are built
// fresh here rather than reusing /payment's AddExitingPayment flow, so
// that: (1) proration reads the real per-user rate instead of a
// hardcoded price, (2) reducing licenses is possible at all — the old
// flow only ever added, (3) a visitor can request an ACH bill instead of
// paying by card immediately. All three go through real, existing,
// already-safe endpoints: PUT paymentsub/subscription/ (updates
// no_of_users, and — by never touching start_date/end_date — new
// licenses stay co-terminus with the current term automatically),
// POST paymentsub/subscription/payment/ (logs the payment or the pending
// ACH request), and paymentsub/subscription/create-payment-intent/ for
// the card charge itself. Cancel still uses the same endpoint /payment
// uses.

const stripePromise = loadStripe(STRIPEPUBLICKEY);

function LicensePaymentStep({ amount, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });
    if (confirmError) {
      setError(confirmError.message || "Payment failed. Please try again.");
      setSubmitting(false);
      return;
    }
    if (
      paymentIntent &&
      (paymentIntent.status === "succeeded" || paymentIntent.status === "requires_capture")
    ) {
      onSuccess();
    } else {
      setError("Payment was not confirmed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <p style={{ color: "#dc2626", fontSize: 12.5, margin: "10px 0 0" }}>{error}</p>}
      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          style={{ ...S.btn, ...S.btnAuto, ...S.btnOutline }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || submitting}
          style={{ ...S.btn, ...S.btnAuto, ...S.btnPrimary }}
        >
          {submitting ? "Confirming Payment..." : `Pay $${amount.toFixed(2)}`}
        </button>
      </div>
    </form>
  );
}

function License() {
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // ── change licenses (increase / reduce) ──────────────────────────────
  const [changeStep, setChangeStep] = useState("closed"); // 'closed' | 'form' | 'payment'
  const [changeDirection, setChangeDirection] = useState("add"); // 'add' | 'reduce'
  const [quantity, setQuantity] = useState(1); // how many licenses to add/remove — never a "new total"
  const [preparingPayment, setPreparingPayment] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [requestingInvoice, setRequestingInvoice] = useState(false);
  const [updatingLicenses, setUpdatingLicenses] = useState(false);

  const notify = (fn, msg) =>
    fn(msg, { position: "top-right", autoClose: 2500, theme: "light" });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [companyRes, subRes, usersRes] = await Promise.all([
        GetCompanyInfoService.getCompanyInfo(),
        axios.get(API + "paymentsub/subscription/", { headers: authHeader() }),
        axios.get(API + "company/users/", { headers: authHeader() }),
      ]);
      setCompany(companyRes.data);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);

      if (subRes.data && subRes.data.success === 1 && subRes.data.data) {
        const sub = subRes.data.data;
        setSubscription(sub);
        const payRes = await axios.get(
          API + `paymentsub/subscription/payment-id/?subscription_id=${sub.id}`,
          { headers: authHeader() }
        );
        setPayments((payRes.data && payRes.data.data) || []);
      }
    } catch (error) {
      // Leave whatever loaded successfully; sections below just won't render.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  if (loading) {
    return <p style={S.subfootnote}>Loading...</p>;
  }

  if (!subscription) {
    return <p style={S.subfootnote}>No subscription found for this account.</p>;
  }

  const perUserAmount = subscription.plan ? Number(subscription.plan.per_user_amount) || 0 : 0;
  const planName = (subscription.plan && subscription.plan.plan_name) || "—";
  const trialDays = Number(subscription.trial_days) || 0;
  const trialEnd = (() => {
    if (!trialDays || !subscription.start_date) return null;
    const d = new Date(subscription.start_date);
    d.setDate(d.getDate() + trialDays);
    return d;
  })();
  const inTrial = perUserAmount > 0 && trialEnd && new Date() <= trialEnd;
  const statusLabel = perUserAmount === 0 ? "Free" : inTrial ? "Trial Period" : "Active";
  const statusStyle = perUserAmount === 0 ? S.badgeMuted : inTrial ? S.badgeWarning : S.badgeSuccess;

  const licenseCount = subscription.no_of_users || 0;
  const registeredCount = users.length;
  const usagePct = licenseCount > 0 ? Math.min((registeredCount / licenseCount) * 100, 100) : 0;
  const atLimit = licenseCount > 0 && registeredCount >= licenseCount;

  // quantity can briefly be "" while the field is being edited (so the
  // visitor can clear the box and type a fresh number instead of it
  // always snapping back to a digit and appending to whatever they type).
  const quantityNum = quantity === "" ? 0 : Number(quantity);
  const delta = changeDirection === "add" ? quantityNum : -quantityNum;
  const newTotal = Math.max(licenseCount + delta, 0);

  // Co-terminus: new licenses are added to the CURRENT term (start_date/
  // end_date are never touched by the update below), so the whole
  // subscription keeps renewing on its existing date instead of a fresh
  // 12 months starting today — but each added license is still billed at
  // the full annual rate, same as any other license on the plan.
  const addAmount = delta > 0 ? perUserAmount * 12 * delta : 0;

  const openChangeLicenses = (direction) => {
    setChangeDirection(direction);
    setQuantity(1);
    setChangeStep("form");
  };
  const closeChangeLicenses = () => {
    setChangeStep("closed");
    setClientSecret("");
  };

  const applyLicenseCount = async (newCount) => {
    const res = await axios.put(
      API + "paymentsub/subscription/",
      { id: subscription.id, no_of_users: newCount },
      { headers: authHeader() }
    );
    if (!res.data || res.data.success !== 1) {
      throw new Error((res.data && res.data.error) || "Couldn't update licenses.");
    }
  };

  const logPayment = async (paymentMethod, paymentGateway, paymentStatus) => {
    await axios.post(
      API + "paymentsub/subscription/payment/",
      {
        sub_id: subscription.id,
        payment_date: new Date().toISOString().slice(0, 10),
        payment_method: paymentMethod,
        payment_gateway: paymentGateway,
        payment_status: paymentStatus,
        no_of_user: delta,
        total_amount: addAmount,
      },
      { headers: authHeader() }
    );
  };

  const handleReduceOrFree = async () => {
    if (delta === 0) return;
    setUpdatingLicenses(true);
    try {
      await applyLicenseCount(newTotal);
      notify(toast.success, "Licenses updated.");
      closeChangeLicenses();
      fetchAll();
    } catch (error) {
      notify(toast.error, error.message || "Couldn't update licenses.");
    } finally {
      setUpdatingLicenses(false);
    }
  };

  const handlePayWithCard = async () => {
    if (delta <= 0) return;
    setPreparingPayment(true);
    try {
      const res = await axios.post(
        API + "paymentsub/subscription/create-payment-intent/",
        { amount: Math.round(addAmount * 100), plan: planName },
        { headers: authHeader() }
      );
      if (res.data && res.data.clientSecret) {
        setClientSecret(res.data.clientSecret);
        setChangeStep("payment");
      } else {
        notify(
          toast.error,
          (res.data && res.data.error) || "Couldn't start the payment. Please try again."
        );
      }
    } catch (error) {
      notify(toast.error, "Couldn't start the payment. Please try again.");
    } finally {
      setPreparingPayment(false);
    }
  };

  const finalizeCardPayment = async () => {
    try {
      await logPayment("STRIPE", "STRIPE", "success");
      await applyLicenseCount(newTotal);
      notify(toast.success, `Added ${quantityNum} license${quantityNum > 1 ? "s" : ""}.`);
      closeChangeLicenses();
      fetchAll();
    } catch (error) {
      notify(
        toast.error,
        "Payment succeeded, but licenses couldn't be updated. Please contact support."
      );
    }
  };

  const handleRequestInvoice = async () => {
    if (delta <= 0) return;
    setRequestingInvoice(true);
    try {
      await logPayment("ACH", "ADMIN", "pending");
      notify(toast.success, "Request recorded — our team will follow up to arrange ACH payment.");
      closeChangeLicenses();
      fetchAll();
    } catch (error) {
      notify(toast.error, "Couldn't record the request. Please try again.");
    } finally {
      setRequestingInvoice(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(
        API + "paymentsub/canceltrial-subscription/",
        {
          user_email: company && company.user_email,
          subscription_id: subscription.id,
          payment_intent_id: subscription.intent_id,
        },
        { headers: authHeader() }
      );
      notify(toast.success, res.data.message || "Subscription cancelled.");
      setTimeout(() => {
        AuthService.logout().then(() => navigate("/"));
      }, 1000);
    } catch (error) {
      notify(toast.error, "Couldn't cancel subscription. Please try again.");
      setCancelling(false);
    }
  };

  return (
    <div>
      <ToastContainer />

      <div style={{ ...S.checkoutGrid, alignItems: "flex-start" }}>
        <div style={{ ...S.planHero, margin: 0 }}>
          <div>
            <p style={S.planHeroLabel}>Your Plan</p>
            <h2 style={S.planHeroName}>{planName}</h2>
          </div>
          <div style={S.planHeroRate}>
            <div style={S.planHeroPrice}>${perUserAmount.toFixed(2)}</div>
            <div style={S.planHeroPricePeriod}>per user / month</div>
          </div>
        </div>

        <div style={{ ...S.panel, margin: 0, maxWidth: "100%", padding: "22px 28px" }}>
          {changeStep === "closed" && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                <div style={S.usageStatsRow}>
                  <div>
                    <div style={S.usageStatNum}>{licenseCount}</div>
                    <div style={S.usageStatLabel}>Licenses</div>
                  </div>
                  <div>
                    <div style={S.usageStatNum}>{registeredCount}</div>
                    <div style={S.usageStatLabel}>In Use</div>
                  </div>
                  <div>
                    <div style={{ ...S.usageStatNum, color: atLimit ? C.warning : C.success }}>
                      {Math.max(licenseCount - registeredCount, 0)}
                    </div>
                    <div style={S.usageStatLabel}>Available</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {subscription.Status !== "C" && perUserAmount > 0 && (
                    <>
                      <button
                        type="button"
                        style={{ ...S.btn, ...S.btnAuto, ...S.btnOutline }}
                        disabled={licenseCount <= 1}
                        onClick={() => openChangeLicenses("reduce")}
                      >
                        Reduce Licenses
                      </button>
                      <button
                        type="button"
                        style={{ ...S.btn, ...S.btnAuto, ...S.btnPrimary }}
                        onClick={() => openChangeLicenses("add")}
                      >
                        Add Licenses
                      </button>
                    </>
                  )}
                  {subscription.intent_id && (
                    <button
                      type="button"
                      style={{ ...S.btn, ...S.btnAuto, background: "#dc2626", color: "#fff" }}
                      onClick={() => setShowCancelConfirm(true)}
                    >
                      Cancel Subscription
                    </button>
                  )}
                </div>
              </div>
              {perUserAmount === 0 && (
                <p style={{ ...S.subfootnote, marginTop: 12, textAlign: "left" }}>
                  If a free organization is not used for 3 months, it may be removed.
                </p>
              )}
            </>
          )}

          {changeStep === "form" && (
            <div>
              <button type="button" style={{ ...S.backLink, marginBottom: 12 }} onClick={closeChangeLicenses}>
                <i className="fa-solid fa-arrow-left" />
                Back
              </button>
              <div style={S.planRow}>
                <div>
                  <div>{changeDirection === "add" ? "Licenses to Add" : "Licenses to Remove"}</div>
                  <div style={{ fontSize: 11.5, color: C.muted }}>
                    Currently {licenseCount} — new total will be {newTotal}
                  </div>
                </div>
                <input
                  type="number"
                  min={1}
                  max={changeDirection === "reduce" ? licenseCount - 1 : undefined}
                  style={S.stepperInput}
                  value={quantity}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      setQuantity("");
                      return;
                    }
                    const n = parseInt(raw, 10);
                    if (!isNaN(n)) setQuantity(n);
                  }}
                  onBlur={() => {
                    if (quantity === "" || Number(quantity) < 1) {
                      setQuantity(1);
                    } else if (changeDirection === "reduce" && Number(quantity) > licenseCount - 1) {
                      setQuantity(Math.max(licenseCount - 1, 1));
                    }
                  }}
                />
              </div>

              {quantityNum > 0 && (
                <div style={{ marginTop: 14 }}>
                  {changeDirection === "add" ? (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>
                        Adding {quantityNum} license{quantityNum > 1 ? "s" : ""}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, marginTop: 10 }}>
                        ${addAmount.toFixed(2)}
                      </div>
                      <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          style={{ ...S.btn, ...S.btnAuto, ...S.btnOutline }}
                          disabled={requestingInvoice || preparingPayment}
                          onClick={handleRequestInvoice}
                        >
                          {requestingInvoice ? "Requesting..." : "Request Bill (ACH)"}
                        </button>
                        <button
                          type="button"
                          style={{ ...S.btn, ...S.btnAuto, ...S.btnPrimary }}
                          disabled={preparingPayment || requestingInvoice}
                          onClick={handlePayWithCard}
                        >
                          {preparingPayment ? "Preparing..." : "Pay with Card"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>
                        Removing {quantityNum} license{quantityNum > 1 ? "s" : ""}
                      </div>
                      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                        Takes effect immediately — no refund is issued for the remaining term.
                      </div>
                      <button
                        type="button"
                        style={{ ...S.btn, ...S.btnAuto, ...S.btnPrimary, marginTop: 12 }}
                        disabled={updatingLicenses}
                        onClick={handleReduceOrFree}
                      >
                        {updatingLicenses ? "Updating..." : "Update Licenses"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {changeStep === "payment" && clientSecret && (
            <div>
              <button type="button" style={{ ...S.backLink, marginBottom: 12 }} onClick={() => setChangeStep("form")}>
                <i className="fa-solid fa-arrow-left" />
                Back
              </button>
              <div style={S.planRow}>
                <span>Amount due today</span>
                <strong>${addAmount.toFixed(2)}</strong>
              </div>
              <div style={{ marginTop: 14 }}>
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <LicensePaymentStep
                    amount={addAmount}
                    onCancel={() => setChangeStep("form")}
                    onSuccess={finalizeCardPayment}
                  />
                </Elements>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ ...S.panel, margin: "0 0 22px", maxWidth: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <h3 style={{ ...S.planTitle, marginBottom: 0 }}>
            <span style={{ ...S.dot, background: "#ef4444" }} />
            Subscription Details
          </h3>
          <span style={{ ...S.badge, ...statusStyle }}>{statusLabel.toUpperCase()}</span>
        </div>
        <div style={S.detailsGrid}>
          <div style={S.detailTile}>
            <div style={S.detailLabel}>Start Date</div>
            <div style={S.detailValue}>
              {subscription.start_date ? new Date(subscription.start_date).toLocaleDateString() : "N/A"}
            </div>
          </div>
          {trialEnd && (
            <div style={S.detailTile}>
              <div style={S.detailLabel}>Trial Period End Date</div>
              <div style={S.detailValue}>{trialEnd.toLocaleDateString()}</div>
            </div>
          )}
          <div style={S.detailTile}>
            <div style={S.detailLabel}>Expiration Date</div>
            <div style={S.detailValue}>
              {perUserAmount > 0 && subscription.end_date
                ? new Date(subscription.end_date).toLocaleDateString()
                : "--"}
            </div>
          </div>
          <div style={S.detailTile}>
            <div style={S.detailLabel}>Renewal Date</div>
            <div style={S.detailValue}>
              {perUserAmount > 0 && subscription.renewal_date
                ? new Date(subscription.renewal_date).toLocaleDateString()
                : "--"}
            </div>
          </div>
          <div style={S.detailTile}>
            <div style={S.detailLabel}>Doc Storage Used</div>
            <div style={S.detailValue}>{(company && company.consumed_doc_storage) || "--"}</div>
          </div>
          <div style={S.detailTile}>
            <div style={S.detailLabel}>Total Storage Used</div>
            <div style={S.detailValue}>{(company && company.consumed_all_storage) || "--"}</div>
          </div>
        </div>
      </div>

      <div style={{ ...S.panel, maxWidth: "100%" }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Payment History</h4>
        {payments.length === 0 ? (
          <p style={S.subfootnote}>No payments yet.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Invoice Date</th>
                  <th style={S.th}>Transaction ID</th>
                  <th style={S.th}>Months</th>
                  <th style={S.th}>Licenses</th>
                  <th style={S.th}>Total Amount</th>
                  <th style={S.th}>Payment Method</th>
                  <th style={S.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, idx) => (
                  <tr key={p.id || idx}>
                    <td style={S.td}>
                      {p.created_at ? new Date(p.created_at).toLocaleDateString() : "--"}
                    </td>
                    <td style={S.td}>{p.payment_ref || p.transaction_id || "--"}</td>
                    <td style={S.td}>{p.months ?? "--"}</td>
                    <td style={S.td}>{p.no_of_user ?? p.no_of_users ?? "--"}</td>
                    <td style={S.td}>${Number(p.total_amount || p.total || 0).toFixed(2)}</td>
                    <td style={S.td}>{p.payment_type || p.payment_method || "N/A"}</td>
                    <td style={S.td}>
                      <span
                        style={{
                          ...S.badge,
                          ...(String(p.payment_status).toLowerCase() === "success"
                            ? S.badgeSuccess
                            : String(p.payment_status).toLowerCase() === "pending"
                            ? S.badgeWarning
                            : S.badgeMuted),
                        }}
                      >
                        {p.payment_status || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCancelConfirm && (
        <div style={S.modalBackdrop} onClick={() => setShowCancelConfirm(false)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <h5 style={S.modalTitle}>Cancel Subscription</h5>
              <button
                type="button"
                style={S.modalClose}
                aria-label="Close"
                onClick={() => setShowCancelConfirm(false)}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div style={S.modalBody}>
              <p style={{ fontSize: 13.5, margin: 0 }}>
                Your subscription for {(company && company.company_name) || "this account"} expires on{" "}
                <strong>
                  {subscription.end_date ? new Date(subscription.end_date).toLocaleDateString() : "N/A"}
                </strong>
                . On confirmation, no further subscription charges will be applied after the expiration
                date. Athena Fintech is not responsible for maintaining your data after that date.
              </p>
            </div>
            <div style={S.modalFooter}>
              <button
                type="button"
                style={{ ...S.btn, ...S.btnAuto, ...S.btnOutline }}
                onClick={() => setShowCancelConfirm(false)}
              >
                Keep Subscription
              </button>
              <button
                type="button"
                disabled={cancelling}
                style={{ ...S.btn, ...S.btnAuto, background: "#dc2626", color: "#fff" }}
                onClick={handleCancelSubscription}
              >
                {cancelling ? "Cancelling..." : "Confirm Cancellation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default License;
